import React from 'react';
import { Input, Switch } from '@material-ui/core';
import {
  Search as SearchIcon,
  RotateRight as LoaderIcon,
} from '@material-ui/icons';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';

const useStyles = makeStyles((theme) => ({
  searchWrapper: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[24],
    backgroundColor: theme.palette.background.paper,
    borderRadius: '28px',
    "transition": 'border-top-left-radius 0.2s ease, border-top-right-radius 0.2s ease',
    ".isOpen &": {
      borderRadius: theme.shape.borderRadius,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    }
  },
  inputContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 3, 1, 3),
  },
  icon: {
    color: theme.palette.text.secondary,
    width: '24px',
    height: '24px',
  },
  searchInput: {
    flex: 1,
    '&.Mui-disabled': {
      cursor: 'wait',
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.text.disabled,
      '& input': {
        cursor: 'wait',
      },
    },
  },
  loadingSpinner: {
    animation: '$spin 1s linear infinite',
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  switchFormGroup: {
    alignItems: 'center',
  },
  switchLabel: {
    fontWeight: '600',
    background: 'linear-gradient(to right, #9810fa, #00b8db)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundPositionX: '0%',
    backgroundSize: '200% 200%',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '&.active': {
      animation: '$gradient 3s linear infinite',
    },
  },
  switchLabelMobile: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  switchEnabledIcon: {
    animation: '$bubbleAppear 0.3s ease-out',
    transformOrigin: 'center',
    color: theme.palette.primary.main,
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  searchInputUnderline: {
    '&:before': {
      borderColor: 'transparent',
    },
    '&:hover:before': {
      borderColor: 'transparent',
    },
  },
  '@keyframes gradient': {
    '0%, 100%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
  },
  '@keyframes bubbleAppear': {
    '0%': {
      opacity: 0,
      transform: 'scale(0.3) rotate(-180deg)',
    },
    '50%': {
      opacity: 0.7,
      transform: 'scale(1.2) rotate(-90deg)',
    },
    '100%': {
      opacity: 1,
      transform: 'scale(1) rotate(0deg)',
    },
  },
}));

/**
 * Search input component with icon and loading spinner
 * @param {Object} props - The component properties
 * @param {string} props.query - The current search value
 * @param {function} props.onQueryChange - Function called when the search changes
 * @param {boolean} props.isLoading - Indicates if a search is in progress
 * @param {boolean} props.toggleEnabled - Indicates if the toggle is enabled
 * @param {function} props.onToggleChange - Function called when the toggle changes
 * @returns {JSX.Element} The search input component
 */
export default function SearchInput({
  query,
  onQueryChange,
  isLoading,
  toggleEnabled,
  onToggleChange,
  ...props
}) {
  const classes = useStyles();

  return (
    <div className={classes.searchWrapper}>
      <div className={classes.inputContainer}>
        {isLoading ? (
          <LoaderIcon className={`${classes.loadingSpinner} ${classes.icon}`} />
        ) : (
          <SearchIcon className={classes.icon} />
        )}

        <Input
          type="text"
          placeholder="What are you looking for ?"
          value={query}
          onChange={onQueryChange}
          className={classes.searchInput}
          classes={{
            underline: classes.searchInputUnderline,
          }}
          {...props}
        />
        <FormGroup row className={classes.switchFormGroup}>
          <FormControlLabel
            control={<Switch color="primary" name="checkedA" />}
            label={
              <span
                className={`${classes.switchLabel} ${toggleEnabled &&
                  'active'}`}
              >
                {toggleEnabled && (
                  <svg
                    className={classes.switchEnabledIcon}
                    xmlns="http://www.w3.org/2000/svg"
                    width="1.2em"
                    height="1.2em"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" />
                    <path d="M20 2v4" />
                    <path d="M22 4h-4" />
                    <circle cx="4" cy="20" r="2" />
                  </svg>
                )}
                AI
                <span className={classes.switchLabelMobile}> search</span>
              </span>
            }
            labelPlacement="start"
            className={classes.switch}
            checked={toggleEnabled}
            onChange={(_, checked) => onToggleChange(checked)}
          />
        </FormGroup>
      </div>
    </div>
  );
}

SearchInput.propTypes = {
  /** The current search value */
  query: PropTypes.string.isRequired,

  /** Function called when the search changes */
  onQueryChange: PropTypes.func.isRequired,

  /** Indicates if the toggle is enabled */
  toggleEnabled: PropTypes.bool.isRequired,

  /** Function called when the toggle changes */
  onToggleChange: PropTypes.func.isRequired,

  /** Indicates if a search is in progress */
  isLoading: PropTypes.bool,
};

SearchInput.defaultProps = {
  isLoading: false,
};
